import {
  ApiError, Button, CONSENT_TYPES, Card, DateInput, ErrorState, Field, Input, PageHeader, PhotoField, Row, Select, Stack,
  invalidDateIn, useApi, useI18n,
  type ConsentType, type CreateChildRequest, type Gender,
} from '@daghfal/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AllergiesEditor, ContactsEditor } from '../components/RegistrationEditors';
import {
  allergiesToRequest, contactsToRequest, emptyContact,
  type AllergyDraft, type ContactDraft,
} from '../components/registrationDrafts';

/**
 * Enrolling a child (FR-ADM-02).
 *
 * Allergies and emergency contacts are part of this form rather than a later step. A child's first day is
 * exactly when both matter most, and a two-stage flow leaves a window where the nursery holds the child
 * but not the allergen. Both are required (Q9): at least one contact, and either the allergies or the
 * family's answer that there are none. The API refuses the form without them and says which is missing.
 *
 * The family's four consents are captured here too, as signed on the form (Q23). They are optional: one left
 * unanswered lists the child under "missing consents", and without individual-photos consent the child is
 * not photographed (Q45).
 */
export function NewChild() {
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t, today } = useI18n();

  const classes = useQuery({
    queryKey: ['classes', { status: 'Active' }],
    queryFn: () => api.classes.list({ status: 'Active' }),
  });

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [preferredName, setPreferredName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState<Gender>('Female');
  const [classId, setClassId] = useState('');
  const [enrollmentDate, setEnrollmentDate] = useState(() => today());
  const [allergies, setAllergies] = useState<AllergyDraft[]>([]);
  const [noKnownAllergies, setNoKnownAllergies] = useState(false);
  // One empty row to start with: nobody is registered without someone to call.
  const [contacts, setContacts] = useState<ContactDraft[]>(() => [emptyContact()]);
  const [photo, setPhoto] = useState<Blob | null>(null);
  const [consents, setConsents] = useState<Partial<Record<ConsentType, boolean>>>({});
  const [consentEvidenceRef, setConsentEvidenceRef] = useState('');

  const create = useMutation({
    mutationFn: async (body: CreateChildRequest) => {
      const child = await api.children.create(body);
      // The photo needs the child to exist first. Once the child does, a failed upload must not keep the
      // form open: sending it again would register the child a second time.
      const photoFailed = photo !== null
        && await api.children.setPhoto(child.id, photo).then(() => false, () => true);
      return { child, photoFailed };
    },
    onSuccess: ({ child, photoFailed }) => {
      void queryClient.invalidateQueries({ queryKey: ['children'] });
      void queryClient.invalidateQueries({ queryKey: ['classes'] });
      navigate(`/children/${child.id}`, { replace: true, state: { photoFailed } });
    },
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const unreadable = invalidDateIn(event.currentTarget);
    if (unreadable) { unreadable.focus(); return; }
    create.mutate({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      preferredName: preferredName.trim() || null,
      dateOfBirth,
      gender,
      classId: classId ? Number(classId) : null,
      enrollmentDate: enrollmentDate || null,
      allergies: noKnownAllergies ? null : allergiesToRequest(allergies),
      emergencyContacts: contactsToRequest(contacts),
      noKnownAllergies,
      consents: CONSENT_TYPES
        .filter((type) => consents[type] !== undefined)
        .map((type) => ({ consentType: type, granted: consents[type]! })),
      consentEvidenceRef: consentEvidenceRef.trim() || null,
    });
  }

  const fieldErrors = create.error instanceof ApiError ? create.error.fieldErrors : {};

  return (
    <>
      <PageHeader
        title={t('children.new.title')}
        actions={<Link className="dg-button dg-button--ghost" to="/children">{t('action.cancel')}</Link>}
      />

      <form onSubmit={submit} noValidate>
        <Stack>
          {create.isError && <ErrorState error={create.error} />}

          <Card title={t('child.title')}>
            <Stack>
              <Row>
                <Field label={t('children.new.firstName')} error={fieldErrors.firstName} required>
                  {(props) => <Input {...props} value={firstName} onChange={(e) => setFirstName(e.target.value)} autoFocus />}
                </Field>
                <Field label={t('children.new.lastName')} error={fieldErrors.lastName} required>
                  {(props) => <Input {...props} value={lastName} onChange={(e) => setLastName(e.target.value)} />}
                </Field>
              </Row>

              <Field label={t('children.new.preferredName')} hint={t('children.new.preferredName.hint')}>
                {(props) => <Input {...props} value={preferredName} onChange={(e) => setPreferredName(e.target.value)} />}
              </Field>

              <Row>
                <Field label={t('children.new.dob')} error={fieldErrors.dateOfBirth} required>
                  {(props) => (
                    <DateInput {...props} value={dateOfBirth} onChange={setDateOfBirth} />
                  )}
                </Field>
                <Field label={t('children.new.gender')} required>
                  {(props) => (
                    <Select {...props} value={gender} onChange={(e) => setGender(e.target.value as Gender)}>
                      <option value="Female">{t('gender.Female')}</option>
                      <option value="Male">{t('gender.Male')}</option>
                    </Select>
                  )}
                </Field>
              </Row>

              <Row>
                <Field label={t('children.new.class')}>
                  {(props) => (
                    <Select {...props} value={classId} onChange={(e) => setClassId(e.target.value)}>
                      <option value="">{t('children.unassigned')}</option>
                      {(classes.data?.items ?? []).map((klass) => (
                        <option key={klass.id} value={klass.id}>
                          {klass.name} — {t('classes.occupancy', {
                            enrolled: klass.activeChildren, capacity: klass.capacity,
                          })}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
                <Field label={t('children.new.enrollmentDate')}>
                  {(props) => (
                    <DateInput {...props} value={enrollmentDate} onChange={setEnrollmentDate} />
                  )}
                </Field>
              </Row>
            </Stack>
          </Card>

          <Card title={t('child.photo')}>
            <PhotoField
              name={preferredName.trim() || firstName.trim()}
              value={photo}
              onChange={setPhoto}
              hint={t('children.new.photo.hint')}
            />
          </Card>

          <AllergiesEditor
            title={t('children.new.allergies')}
            allergies={allergies}
            setAllergies={setAllergies}
            noneKnown={noKnownAllergies}
            setNoneKnown={setNoKnownAllergies}
          />

          <ContactsEditor title={t('children.new.emergencyContacts')} contacts={contacts} setContacts={setContacts} />

          <Card title={t('consent.registration')} hint={t('consent.registration.hint')}>
            <Stack>
              <Field label={t('consent.evidence')} hint={t('consent.evidence.hint')}>
                {(props) => (
                  <Input {...props} value={consentEvidenceRef} onChange={(e) => setConsentEvidenceRef(e.target.value)} />
                )}
              </Field>
              {CONSENT_TYPES.map((type) => (
                <Field key={type} label={t(`consent.${type}`)}>
                  {(props) => (
                    <Select
                      {...props}
                      value={consents[type] === undefined ? '' : String(consents[type])}
                      onChange={(e) => setConsents((current) => ({
                        ...current,
                        [type]: e.target.value === '' ? undefined : e.target.value === 'true',
                      }))}
                    >
                      <option value="">{t('consent.unanswered')}</option>
                      <option value="true">{t('consent.granted')}</option>
                      <option value="false">{t('consent.notGranted')}</option>
                    </Select>
                  )}
                </Field>
              ))}
            </Stack>
          </Card>

          <Row>
            <Button type="submit" size="large" busy={create.isPending} busyLabel={t('action.saving')}>
              {t('children.new.submit')}
            </Button>
          </Row>
        </Stack>
      </form>
    </>
  );
}

